import React, { useRef, useEffect } from 'react'
import { connect } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import _ from 'lodash'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import BigBreadcrumbs from '../../../components/layout/navigation/components/BigBreadcrumbs'
import WidgetGrid from '../../../components/layout/widgets/WidgetGrid'
import JarvisWidget from '../../../components/layout/widgets/JarvisWidget'
import Datatable from '../../../components/tables/Datatable'
import { fetchRetailers } from '../../../common/actions/retailer'
import { statusCodeToText } from '../../../common/utils/retailer'
import { showSavedMessage } from '../../../common/utils/savedMessage'
import { LIST_PAGE_LENGTH } from '../../../common/constants'
import history from '../../../common/history'

import styles from './List.module.css'
import classNames from 'classnames'

const renderName = name => `
  <div class="${styles.nameColumn}">
    <div class="${styles.expandButton}">+</div>
    <span>${name}</span>
  </div>
`

const renderStatus = statusCode => _.capitalize(statusCodeToText(statusCode))

const renderVerified = data => (data.verified ? 'Yes' : 'No')

const renderCity = (data, type, row) => {
  if (data) {
    return data
  }
  if (row['gmapSublocality']) {
    return row['gmapSublocality']
  }
  for (let i = 1; i <= 5; i++) {
    if (row[`gmapSublocality${i}`]) {
      return row[`gmapSublocality${i}`]
    }
  }
  return ''
}

function RetailerList({ retailers, retailerInProgress, pageSize, fetchRetailers }) {
  const navigate = useNavigate()
  const location = useLocation()
  const containerRef = useRef(null)
  const prevLocationRef = useRef(location)

  useEffect(() => {
    const handleSpaClick = e => {
      const anchor = e.target.closest('a[data-spa-href]')
      if (!anchor) return
      e.preventDefault()
      navigate(anchor.getAttribute('data-spa-href'))
    }
    containerRef.current?.addEventListener('click', handleSpaClick)
    return () => containerRef.current?.removeEventListener('click', handleSpaClick)
  }, [navigate])

  // The add/edit/delete form redirects here with ?saved=<key> so the
  // confirmation survives the navigation (this page owns the ToastContainer).
  useEffect(() => {
    showSavedMessage({
      created: 'Retailer added successfully!',
      updated: 'Retailer updated successfully!',
      deleted: 'Retailer deleted successfully!'
    })
  }, [])

  useEffect(() => {
    const prevLocation = prevLocationRef.current
    prevLocationRef.current = location
    const prevQuery = new URLSearchParams(prevLocation.search).get('page')
    const nextQuery = new URLSearchParams(location.search).get('page')
    if (!retailers || prevQuery === nextQuery || retailerInProgress) return
    const nextPage = parseInt(nextQuery, 10)
    let page = retailers.getMeta('pageNumber')
    if (page === 0 || !page) page = 1
    if (nextPage !== page) {
      const sp = new URLSearchParams(location.search)
      sp.set('page', page)
      if (nextQuery) {
        navigate({ pathname: location.pathname, search: sp.toString() })
      } else {
        navigate({ pathname: location.pathname, search: sp.toString() }, { replace: true })
      }
    }
  }, [location, retailers, retailerInProgress])

  const query = new URLSearchParams(location.search)
  let page = retailers ? retailers.getMeta('pageNumber') : parseInt(query.get('page'))
  page = _.isInteger(page) ? Math.max(page, 1) : 1
  const displayStart = (page - 1) * pageSize
  // Where the add/edit form returns to. A consumed ?saved= flag is only
  // stripped from the browser url, not from the router location, so drop it
  // here or it rides along in the backUrl.
  const listSearch = new URLSearchParams(location.search)
  listSearch.delete('saved')
  const listUrl = location.pathname + (listSearch.toString() ? `?${listSearch}` : '')

  const renderId = row => {
    const status = statusCodeToText(row.statusCode)
    if (status && status.toLowerCase() === 'deleted') return ''
    if (retailers) {
      const q = retailers.getMeta('query')
      if (q) query.set('query', q)
      else query.delete('query')
    }
    // A consumed ?saved= flag is only stripped from the browser url, not from
    // the router location, so drop it here or it rides along in the backUrl.
    query.delete('saved')
    const backUrl = encodeURIComponent(location.pathname + (query.toString() ? '?' + query.toString() : ''))
    const [createdDay, retailerUuid] = row.id.split('/')
    const href = `/retailers/${encodeURIComponent(createdDay)}/${encodeURIComponent(retailerUuid)}/edit?backUrl=${backUrl}`
    return `<a href="${href}" data-spa-href="${href}">Edit</a>`
  }

  return (
    <div id="content" className={styles.listWrapper} ref={containerRef}>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover />
      <div className="row">
        <BigBreadcrumbs
          items={['Retailers']}
          icon="table"
          className="col-xs-12 col-sm-7 col-md-7 col-lg-4"
        />
        <button
          className={`btn btn-primary pull-right ${styles.addButton}`}
          onClick={() => navigate(`/retailers/new?backUrl=${encodeURIComponent(listUrl)}`)}
        >
          Add a Retailer
        </button>
      </div>
      <WidgetGrid>
        <div className="row">
          <article className="col-sm-12">
            <JarvisWidget editbutton={false} togglebutton={false} fullscreenbutton={false} deletebutton={false} color="darken">
              <header>
                <span className="widget-icon"><i className="fa fa-list" /></span>
                <h2>Retailers</h2>
              </header>
              <div>
                <div className="widget-body no-padding">
                  <Datatable
                    options={{
                      processing: true,
                      serverSide: true,
                      ajax: (q, callback) => fetchRetailers(q, callback),
                      lengthChange: false,
                      pageLength: pageSize,
                      displayStart,
                      search: { search: query.get('query') },
                      columns: [
                        { data: 'name', render: renderName },
                        { data: 'statusCode', render: renderStatus },
                        { data: null, render: renderVerified },
                        { data: 'gmapStreetAddr' },
                        { data: 'gmapLocality', render: renderCity },
                        { data: 'gmapCountry' },
                        { data: 'phone' },
                        { data: 'email' },
                        { data: null, render: renderId }
                      ],
                      autoWidth: false,
                      responsive: { details: { type: 'inline' } }
                    }}
                    searchPlaceholder="Name or email"
                    className={classNames('table table-striped table-bordered table-hover', styles.table)}
                    width="100%"
                  >
                    <thead>
                      <tr>
                        <th className="all">Name</th>
                        <th data-priority="2">Status</th>
                        <th data-priority="4">Verified</th>
                        <th data-priority="6">Street Address</th>
                        <th data-priority="5">City</th>
                        <th data-priority="6">Country</th>
                        <th data-priority="5">Phone</th>
                        <th data-priority="4">Email</th>
                        <th data-priority="1" className={styles.actionsColumn}>Actions</th>
                      </tr>
                    </thead>
                  </Datatable>
                </div>
              </div>
            </JarvisWidget>
          </article>
        </div>
      </WidgetGrid>
    </div>
  )
}

const mapStateToProps = ({ retailer }) => {
  const retailers = retailer.get('retailers')
  const perPage = retailers && retailers.getMeta('perPage')
  return {
    retailers,
    retailerInProgress: retailer.get('inProgress'),
    pageSize: perPage || LIST_PAGE_LENGTH
  }
}

const mapDispatchToProps = dispatch => ({
  fetchRetailers(query, callback) {
    dispatch(
      fetchRetailers(query, retailers => {
        setTimeout(() => {
          try {
            if (!retailers) {
              callback({ recordsTotal: 0, recordsFiltered: 0, data: [] })
              return
            }
            const recordsTotal = retailers.getMeta('recordsTotal') || 0
            callback({
              recordsTotal,
              recordsFiltered: recordsTotal,
              data: (retailers.toJSON() || []).map(item => _.extend(item.attributes, { id: item.id }))
            })
          } catch (e) {
            console.error('DataTable fetch error:', e)
            callback({ recordsTotal: 0, recordsFiltered: 0, data: [] })
          }
        })
      })
    )
  }
})

export default connect(mapStateToProps, mapDispatchToProps)(RetailerList)
